// The shop and the orbs: what can be bought, what an orb can give and how a
// pull is rolled. Pure functions; AccountDO rolls with secureRng and writes
// the coins and the unlock in one transaction.
import { z } from "zod";
import { ORB_DUPLICATE_REFUND_RATE, ORB_ODDS, ORB_TIERS, type OrbTier, type Rarity } from "./economy";
import { RARITY_ORDER, SHOP_ITEMS, type ItemDef } from "./items";
import { ItemIdSchema, MeSchema } from "./protocol";
import type { Rng } from "./rng";

export const OrbTierSchema = z.enum(ORB_TIERS as [OrbTier, ...OrbTier[]]);

/** POST /api/me/shop/buy */
export const BuyItemBodySchema = z.object({ itemId: ItemIdSchema });
/** POST /api/me/orbs/open — `free` spends a free pull (weekly rewards) instead of coins. */
export const OpenOrbBodySchema = z.object({ tier: OrbTierSchema, free: z.boolean().optional() });

export const OrbResultSchema = z.object({
  tier: OrbTierSchema,
  itemId: ItemIdSchema,
  /** You already owned it: you got `refund` coins instead. */
  duplicate: z.boolean(),
  refund: z.number().int(),
  me: MeSchema,
});
export type OrbResult = z.infer<typeof OrbResultSchema>;

/** Odds are percents (whole numbers); rolls are made out of 10 000, so two decimals would work too. */
const ODDS_SCALE = 100;

/** Items an orb can give for a rarity (everything the shop sells). */
export function orbPool(rarity: Rarity): ItemDef[] {
  return SHOP_ITEMS.filter((i) => i.rarity === rarity);
}

/** Pick a rarity by the tier's odds. */
export function rollRarity(rng: Rng, tier: OrbTier): Rarity {
  const odds = ORB_ODDS[tier];
  const order = [...RARITY_ORDER].reverse(); // common first
  const total = order.reduce((n, r) => n + Math.round(odds[r] * ODDS_SCALE), 0);
  let roll = rng.int(total);
  for (const r of order) {
    roll -= Math.round(odds[r] * ODDS_SCALE);
    if (roll < 0) return r;
  }
  return "common";
}

/** One orb pull: a rarity by the odds, then any item of that rarity with equal chance. */
export function rollOrb(rng: Rng, tier: OrbTier): ItemDef {
  const rarity = rollRarity(rng, tier);
  // If a rarity had no items, fall back to the next more common one.
  const fallbacks = RARITY_ORDER.slice(RARITY_ORDER.indexOf(rarity));
  for (const r of fallbacks) {
    const pool = orbPool(r);
    if (pool.length > 0) return pool[rng.int(pool.length)]!;
  }
  throw new Error("the shop has no items");
}

/** Coins paid back for an orb item you already own: 1/3 of its shop price, rounded down. */
export function duplicateRefund(item: ItemDef): number {
  return Math.floor((item.price ?? 0) * ORB_DUPLICATE_REFUND_RATE);
}

/** Average shop value of one pull (for the docs and tests). */
export function orbAverageValue(tier: OrbTier): number {
  let total = 0;
  for (const r of RARITY_ORDER) {
    const pool = orbPool(r);
    if (pool.length === 0) continue;
    const avg = pool.reduce((n, i) => n + (i.price ?? 0), 0) / pool.length;
    total += (ORB_ODDS[tier][r] / 100) * avg;
  }
  return total;
}

